// utils/semesterUtils.ts
/**
 * Unified Semester Utilities
 *
 * This is the single source of truth for all semester formatting and conversion.
 * Consolidates functionality from:
 * - semesterFormat.js (deprecated)
 * - courseUtils.js formatSemesterName (deprecated)
 *
 * Semester formats:
 * - Backend/numeric: "202408" (YYYYMM)
 * - Frontend/readable: "fall-2024"
 * - Display: "Fall 2024"
 *
 * Special semesters:
 * - 000008: AP/Transfer Credits (displays as "AP Credits")
 * - 000009: Other Credits (displays as "Other Credits")
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/** Term code mapping (month codes used in backend format) */
export const TERM_CODES = {
  SPRING: '01',
  SUMMER: '05',
  FALL: '08',
  WINTER: '12'
};

/** Reverse mapping for converting display names back to term codes */
export const DISPLAY_TO_TERM_CODE = {
  'Spring': '01',
  'Summer': '05',
  'Fall': '08',
  'Winter': '12'
};

/** Special semester codes for prior learning credits */
export const SPECIAL_SEMESTER_CODES = {
  AP_CREDITS: '000008',
  OTHER_CREDITS: '000009'
};

/** Legacy transfer semester values (for backward compatibility) */
export const TRANSFER_SEMESTER_VALUES = ['-1', '0', '000000', '000008'];

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Check if a string is in numeric semester code format
 * @param {string} code - String to check
 * @returns {boolean} True if numeric format (YYYYMM)
 */
export const isNumericSemesterCode = (code: string): boolean => {
  return /^\d{6}$/.test(code);
};

/**
 * Check if a semester code represents AP/Transfer Credits
 * @param {string} semester - Semester code in any format
 * @returns {boolean} True if AP/Transfer credits
 */
export const isAPCreditsSemester = (semester: string | null | undefined): boolean => {
  if (!semester) return false;
  const normalized = String(semester).toLowerCase();
  return normalized === '000008' ||
         normalized === 'transfer-0000' ||
         normalized === 'transfer' ||
         normalized === '8' ||
         normalized === '-1';
};

/**
 * Check if a semester code represents Other Credits
 * @param {string} semester - Semester code in any format
 * @returns {boolean} True if Other credits
 */
export const isOtherCreditsSemester = (semester: string | null | undefined): boolean => {
  if (!semester) return false;
  const normalized = String(semester).toLowerCase();
  return normalized === '000009' ||
         normalized === '9' ||
         normalized === 'other-credits';
};

/**
 * Check if a semester code represents any prior credits (AP or Other)
 * @param {string} semester - Semester code in any format
 * @returns {boolean} True if prior credits semester
 */
export const isPriorCreditsSemester = (semester: string | null | undefined): boolean => {
  return isAPCreditsSemester(semester) || isOtherCreditsSemester(semester);
};

/**
 * Convert frontend semester format to backend format
 * @param {string} semester - Frontend format (e.g., "fall-2024") or backend format
 * @returns {string} Backend format (e.g., "202408")
 */
export const toBackendSemesterCode = (semester: string | null | undefined): string => {
  if (!semester || typeof semester !== 'string') {
    console.error('Invalid semester:', semester);
    return semester as any;
  }

  // Handle all transfer/AP credit aliases first
  const normalizedSemester = semester.toLowerCase().replace(/ /g, '-');
  const transferAliases = [
    'transfer', 'transfers', 'transfer-credits', 'transfer-credit',
    'ap', 'aps', 'transfer-0000', '8', '-1'
  ];

  if (transferAliases.includes(normalizedSemester)) {
    return '000008';
  }

  // Handle Other Credits aliases
  const otherCreditsAliases = ['9', 'other', 'other-credits'];
  if (otherCreditsAliases.includes(normalizedSemester)) {
    return '000009';
  }

  // If already in backend format, return as is
  if (isNumericSemesterCode(semester)) {
    return semester;
  }

  // Parse frontend format
  const parts = semester.toLowerCase().split('-');
  if (parts.length !== 2) {
    console.error('Invalid semester format:', semester);
    return semester;
  }

  const [season, year] = parts;
  
  const seasonMap: Record<string, string> = {
    'spring': '01',
    'summer': '05',
    'fall': '08',
    'winter': '12'
  };

  const monthCode = seasonMap[season];
  if (!monthCode) {
    console.error('Unknown season:', season);
    return semester;
  }

  // For winter, it's December of the previous year
  const adjustedYear = season === 'winter' ? parseInt(year) - 1 : year;
  
  return `${adjustedYear}${monthCode}`;
};

/**
 * Convert backend semester format to frontend format
 * @param {string} semesterCode - Backend format (e.g., "202408") or frontend format
 * @returns {string} Frontend format (e.g., "fall-2024")
 */
export const fromBackendSemesterCode = (semesterCode: string | null | undefined): string => {
  if (!semesterCode || typeof semesterCode !== 'string') {
    return semesterCode as any;
  }

  // Handle AP/transfer semester special cases - return just 'transfer'
  if (semesterCode === '000008' || semesterCode === 'transfer-0000' || semesterCode === '8') {
    return 'transfer';
  }

  // Handle Other Credits semester - return 'other-credits'
  if (semesterCode === '000009' || semesterCode === '9') {
    return 'other-credits';
  }

  // If already in frontend format, return as is
  if (semesterCode.includes('-')) {
    return semesterCode;
  }

  if (semesterCode.length !== 6) {
    console.error('Invalid semester code:', semesterCode);
    return semesterCode;
  }

  const year = semesterCode.substring(0, 4);
  const month = semesterCode.substring(4, 6);
  
  const monthToSeason: Record<string, string> = {
    '01': 'spring',
    '05': 'summer',
    '08': 'fall',
    '12': 'winter'
  };

  const season = monthToSeason[month];
  if (!season) {
    console.error('Unknown month code:', month);
    return semesterCode;
  }

  // For winter, add 1 to the year (December 2024 = winter-2025)
  const displayYear = season === 'winter' ? parseInt(year) + 1 : year;
  
  return `${season}-${displayYear}`;
};

/**
 * Get display name for a semester
 * @param {string} semester - Either format
 * @returns {string} Display name (e.g., "Fall 2024")
 */
export const getSemesterDisplayName = (semester: string | number | null | undefined | false): string | null => {
  // Handle null/undefined/false
  if (semester === null || semester === undefined || semester === false) {
    return null;
  }

  // Convert to string for consistent handling
  const semesterStr = String(semester);

  // Handle AP/transfer semester special cases (includes -1 which was legacy transfer code)
  if (semesterStr === 'transfer-0000' || semesterStr === '000008' ||
      semesterStr === 'transfer' || semesterStr === '8' || semesterStr === '-1') {
    return 'AP Credits';
  }

  // Handle Other Credits special cases
  if (semesterStr === '000009' || semesterStr === '9' || semesterStr === 'other-credits') {
    return 'Other Credits';
  }

  // Convert to readable format first if needed
  const readableSemester = isNumericSemesterCode(semesterStr)
    ? fromBackendSemesterCode(semesterStr)
    : semesterStr;

  // Handle cases that might come from conversion
  if (readableSemester === 'transfer') {
    return 'AP Credits';
  }
  if (readableSemester === 'other-credits') {
    return 'Other Credits';
  }

  if (!readableSemester || !readableSemester.includes('-')) {
    // Return original semester as string for unknown formats
    return semesterStr;
  }

  const [season, year] = readableSemester.split('-');
  const seasonCapitalized = season.charAt(0).toUpperCase() + season.slice(1);
  return `${seasonCapitalized} ${year}`;
};

/**
 * Compare two semesters chronologically (works with both formats)
 * @param {string} a - First semester
 * @param {string} b - Second semester
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export const compareSemesters = (a: string, b: string): number => {
  // Handle special credit semesters - they come first in order: AP Credits, Other Credits
  const aIsAP = a === 'transfer-0000' || a === '000008' || a === 'transfer' || a === '8';
  const bIsAP = b === 'transfer-0000' || b === '000008' || b === 'transfer' || b === '8';
  const aIsOther = a === '000009' || a === '9' || a === 'other-credits';
  const bIsOther = b === '000009' || b === '9' || b === 'other-credits';

  // AP Credits comes first
  if (aIsAP && !bIsAP) return -1;
  if (!aIsAP && bIsAP) return 1;
  if (aIsAP && bIsAP) return 0;

  // Other Credits comes after AP but before regular semesters
  if (aIsOther && !bIsOther) return -1;
  if (!aIsOther && bIsOther) return 1;
  if (aIsOther && bIsOther) return 0;

  // Convert both to numeric format for comparison
  const aNumeric = toBackendSemesterCode(a);
  const bNumeric = toBackendSemesterCode(b);

  if (aNumeric < bNumeric) return -1;
  if (aNumeric > bNumeric) return 1;
  return 0;
};

/**
 * Sort an array of semester entries by semester code
 * @param {Array} entries - Array of [semester, data] pairs
 * @returns {Array} Sorted array
 */
export const sortSemesterEntries = <V>(entries: [string, V][]): [string, V][] => {
  return entries.sort(([a], [b]) => compareSemesters(a, b));
};

/**
 * Convert numeric semester keys to frontend format in an object
 * @param {Object} obj - Object with semester keys
 * @returns {Object} Object with converted keys
 */
export const convertSemesterKeys = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const convertedKey = isNumericSemesterCode(key) ? fromBackendSemesterCode(key) : key;
    result[convertedKey] = value;
  }
  return result;
};

/**
 * Convert frontend semester keys to numeric format in an object
 * @param {Object} obj - Object with semester keys
 * @returns {Object} Object with converted keys
 */
export const convertToNumericSemesterKeys = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const convertedKey = !isNumericSemesterCode(key) ? toBackendSemesterCode(key) : key;
    result[convertedKey] = value;
  }
  return result;
};

